import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PageShell } from "./publicShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { trackEvent, INDUSTRIES } from "@/mock";
import { analyticsApi, crmApi } from "@/services/phase1Api";
import {
  ArrowRight, Check, ShieldCheck, FileCheck2, Lock, Zap,
  BookOpen, Bell, FileSearch, Link2, Route,
  CheckCircle2, FileText, Sparkles, Calendar,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   EXECUTIVE CLARITY — 589 Labs.ai Landing Page
   ═══════════════════════════════════════════════════════════════ */

const scrollToIntake = () => {
  const el = document.getElementById("opportunity-score");
  if (el) {
    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

/* Shared heading classes — lighter weight per user feedback */
const H2 = "text-[32px] sm:text-[38px] lg:text-[42px] font-bold text-[#111827] leading-[1.15] tracking-tight";
const EYEBROW = "text-[12px] font-semibold tracking-[0.16em] text-[#1a56db] uppercase mb-3";

/* ─── HERO ─────────────────────────────────────────────────── */
const HeroSection = () => (
  <section className="bg-white" data-testid="hero-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 pt-16 pb-14 lg:pt-24 lg:pb-20">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div>
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-4 py-1.5 text-[13px] font-semibold text-[#1a56db] mb-7"
            data-testid="hero-audience-pill"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
            AI Builders for Operations Leaders
          </div>
          <h1 className="text-[38px] sm:text-[44px] lg:text-[50px] font-bold text-[#111827] leading-[1.1] tracking-tight">
            <span className="whitespace-nowrap">Multiply productivity —</span>
            <br />
            <span className="whitespace-nowrap">without adding headcount.</span>
          </h1>
          <p className="mt-6 text-[17px] text-[#4b5563] leading-[1.6] max-w-[560px]">
            We install AI workflow automation that reads, decides, and acts
            with the right approvals — so you get more output without more
            overhead.
          </p>
          <div className="mt-9">
            <Button
              onClick={scrollToIntake}
              className="h-14 rounded-xl bg-[#1a56db] px-7 text-white text-[15px] font-semibold hover:bg-[#143faa] shadow-none"
              data-testid="hero-cta-primary"
            >
              Get My AI Opportunity Score
              <ArrowRight className="ml-2 h-[18px] w-[18px]" />
            </Button>
            <p className="mt-4 text-[13px] text-[#9ca3af]">
              Free assessment • Results in 48 hours
            </p>
          </div>
        </div>

        {/* RIGHT — Opportunity Snapshot card */}
        <div
          className="bg-white border border-[#e5e7eb] rounded-2xl p-8 shadow-[0_4px_24px_rgba(17,24,39,0.06)]"
          data-testid="opportunity-snapshot-card"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="text-[11px] font-bold tracking-[0.14em] text-[#9ca3af] uppercase">
              Opportunity Snapshot
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#ecfdf5] px-3 py-1 text-[11px] font-semibold text-[#059669]">
              Sample Report
            </span>
          </div>
          <div className="text-[14px] text-[#4b5563] mb-1">Estimated annual time saved</div>
          <div className="text-[54px] font-bold text-[#111827] tracking-tight leading-[1]">
            2,400 <span className="text-[28px] font-semibold text-[#4b5563]">hrs</span>
          </div>

          <div className="mt-9 space-y-8">
            {[
              { label: "Invoice processing", value: 85 },
              { label: "Client onboarding", value: 72 },
              { label: "Compliance reporting", value: 60 },
            ].map((bar) => (
              <div key={bar.label}>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[14px] font-medium text-[#111827]">{bar.label}</span>
                  <span className="text-[14px] font-semibold text-[#4b5563]">{bar.value}%</span>
                </div>
                <div className="h-[8px] rounded-full bg-[#f3f4f6] overflow-hidden">
                  <div className="h-full rounded-full bg-[#1a56db]" style={{ width: `${bar.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── TRUST BAR ────────────────────────────────────────────── */
const TrustBar = () => (
  <section className="bg-[#f9fafb] border-y border-[#e5e7eb]" data-testid="trust-bar">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-7">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: ShieldCheck, text: "Least-privilege security" },
          { icon: FileCheck2, text: "Full audit trails" },
          { icon: Lock, text: "Human approval gates" },
          { icon: Zap, text: "Production-grade controls" },
        ].map((item) => (
          <div key={item.text} className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#ecfdf5] flex items-center justify-center shrink-0">
              <item.icon className="h-[18px] w-[18px] text-[#10b981]" />
            </div>
            <span className="text-[14px] font-semibold text-[#111827]">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── AI AGENTS & ORCHESTRATION — full-width visual band ──────────── */
const AIOrchestrationGraphic = () => (
  <section
    className="bg-white pt-10 lg:pt-12 pb-2 lg:pb-4"
    data-testid="ai-orchestration-section"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div
        className="rounded-2xl border border-[#e5e7eb] bg-white overflow-hidden"
        data-testid="ai-orchestration-graphic"
      >
        {/* HEADER — sits inside the card so the band aligns with the rest of the page */}
        <div className="px-6 sm:px-8 lg:px-10 pt-7 sm:pt-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#3B4FE4]">
                AI Agents &amp; Orchestration
              </div>
              <h2
                className="mt-2 text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F1117] leading-[1.15] tracking-tight"
                style={{ letterSpacing: "-0.5px" }}
              >
                One brain. Many hands. Your rules.
              </h2>
            </div>
            <p className="text-[14px] sm:text-[14.5px] text-[#5C6070] leading-[1.6] max-w-md">
              Orchestrated AI doesn&apos;t replace your team — it handles the
              coordination work so your people focus on what only humans can do.
            </p>
          </div>
        </div>

        {/* THE 3 STAGE CARDS */}
        <div className="px-6 sm:px-8 lg:px-10 pt-7 pb-8">
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                n: "1",
                eyebrow: "The trigger",
                title: "Something happens in your business",
                body: "A lead arrives. An invoice lands. A student misses class. The AI notices — immediately, every time.",
                bg: "#E6EFFE", iconBg: "#3B82F6",
                iconPath: <><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/></>
              },
              {
                n: "2",
                eyebrow: "The orchestrator",
                title: "The right agent gets the right task",
                body: "An orchestrator reads the context, applies your rules, and routes work to the specialist agent best suited for it.",
                bg: "#EBE7FA", iconBg: "#7C5CF7",
                iconPath: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></>
              },
              {
                n: "3",
                eyebrow: "The outcome",
                title: "Action happens — with your approval",
                body: "Drafts written. Data pulled. Alerts sent. Sensitive steps wait for your sign-off. ROI measured from day one.",
                bg: "#E5F5EB", iconBg: "#16A34A",
                iconPath: <><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M2 10h20M12 14h.01"/></>
              },
            ].map((c) => (
              <div
                key={c.n}
                className="relative rounded-xl p-5 sm:p-6 overflow-hidden"
                style={{ background: c.bg, minHeight: 200 }}
                data-testid={`ai-orch-card-${c.n}`}
              >
                <div
                  className="absolute right-3 top-1 select-none pointer-events-none"
                  style={{ fontSize: 80, fontWeight: 800, color: "rgba(15,17,23,0.07)", lineHeight: 1, letterSpacing: "-0.04em" }}
                >
                  {c.n}
                </div>
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center text-white"
                  style={{ background: c.iconBg }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    {c.iconPath}
                  </svg>
                </div>
                <div className="mt-4 text-[10.5px] font-bold tracking-[0.16em] uppercase text-[#5C6070]">
                  {c.eyebrow}
                </div>
                <div className="mt-1.5 text-[16px] sm:text-[17px] font-bold text-[#0F1117] leading-[1.25]" style={{ letterSpacing: "-0.01em" }}>
                  {c.title}
                </div>
                <p className="mt-3 text-[13px] sm:text-[13.5px] text-[#5C6070] leading-[1.55]">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* STAT STRIP */}
        <div className="border-t border-[#e5e7eb] grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#e5e7eb]">
          {[
            { v: "24/7",  l: "Always watching your workflows" },
            { v: "0",     l: "Handoffs dropped or forgotten" },
            { v: "100%",  l: "Audit trail for every action" },
            { v: "Day 1", l: "ROI tracked from the start" },
          ].map((s) => (
            <div key={s.l} className="px-4 sm:px-6 py-5 text-center">
              <div className="text-[24px] sm:text-[26px] font-bold text-[#0F1117] tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                {s.v}
              </div>
              <div className="mt-1 text-[11px] sm:text-[12px] text-[#5C6070] leading-[1.4]">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        {/* TRUST CHIPS + INTEGRATION CHIPS */}
        <div className="px-6 sm:px-8 lg:px-10 py-5 bg-[#FAFAF8] border-t border-[#e5e7eb] flex flex-wrap items-center gap-2 justify-center">
          {["Works with your existing systems", "Human approval on sensitive steps", "No rip-and-replace required"].map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-3 py-1 rounded-full border border-[#3B4FE4]/30 text-[11.5px] font-semibold text-[#3B4FE4] bg-white"
            >
              {t}
            </span>
          ))}
          <span className="hidden sm:inline-block w-px h-4 bg-[#e5e7eb] mx-1" />
          {["CRM", "ERP", "Email", "Documents", "Any API"].map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-3 py-1 rounded-full border border-[#e5e7eb] text-[11.5px] font-semibold text-[#5C6070] bg-white"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── WHAT WE ACTUALLY BUILD ──────────────────────────────── */
const WhatWeBuild = () => (
  <section id="services-section" className="bg-white py-20 lg:py-28" data-testid="what-we-build-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Left — Copy */}
        <div>
          <div className={EYEBROW}>What we actually build</div>
          <h2 className={H2}>
            AI agents that work inside your business — not beside it.
          </h2>
          <p className="mt-6 text-[16px] text-[#4b5563] leading-[1.7]">
            Instead of bolting another chatbot onto your stack, we build
            orchestrated AI agents that operate like skilled digital employees —
            reading from your systems, making decisions with your rules, and
            acting with the right approvals.
          </p>

          {/* AI Orchestration graphic moved above this section — see <AIOrchestrationGraphic /> */}

          <h3 className="mt-10 text-[22px] font-bold text-[#111827] tracking-tight">
            What is AI orchestration?
          </h3>
          <p className="mt-4 text-[16px] text-[#4b5563] leading-[1.7]">
            <span className="font-semibold text-[#111827]">Orchestration</span>{" "}
            means coordinating multiple AI agents, system integrations, and
            human approvals into a single reliable workflow that runs across
            the systems you already use.
          </p>
          <p className="mt-4 text-[16px] text-[#4b5563] leading-[1.7]">
            Instead of replacing your team, orchestrated AI agents{" "}
            <span className="font-semibold text-[#111827]">handle the</span>{" "}
            repetitive steps — reading emails, pulling data, drafting
            responses, routing approvals — so your people can focus on
            judgment, relationships, and strategy.
          </p>

          {/* Spaced bullets — more breathing room */}
          <div className="mt-12 space-y-9">
            {[
              {
                icon: Link2,
                title: "Works with your existing systems",
                desc: "Your ERP, CRM, email, and documents — agents connect through approved integrations, not workarounds.",
              },
              {
                icon: Lock,
                title: "You stay in control",
                desc: "Every agent has defined boundaries. Sensitive actions require human approval. Nothing runs without your permission.",
              },
              {
                icon: CheckCircle2,
                title: "Measurable from day one",
                desc: "Every workflow tracks time saved, error reduction, and throughput — so you see ROI, not just activity.",
              },
            ].map((f) => (
              <div key={f.title} className="flex gap-5">
                <div className="h-11 w-11 rounded-lg bg-[#e8eefb] flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-[#1a56db]" />
                </div>
                <div className="pt-1">
                  <div className="text-[16px] font-bold text-[#111827]">{f.title}</div>
                  <div className="mt-2 text-[14.5px] text-[#4b5563] leading-[1.65] max-w-[460px]">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Workflow card (doubled step spacing) */}
        <div
          className="bg-[#f9fafb] border border-[#e5e7eb] rounded-2xl p-8 lg:p-10 lg:sticky lg:top-28"
          data-testid="workflow-card"
        >
          <div className="text-[11px] font-bold tracking-[0.14em] text-[#9ca3af] uppercase mb-8">
            How an AI agent handles a workflow
          </div>

          <div className="space-y-10">
            {[
              { icon: Bell, title: "Trigger", desc: "New invoice arrives in shared inbox" },
              { icon: FileSearch, title: "Read", desc: "Agent extracts vendor, amount, PO number" },
              { icon: Link2, title: "Match", desc: "Cross-references against your ERP records" },
              { icon: Route, title: "Route", desc: "Flags exceptions, sends clean invoices for approval" },
              { icon: CheckCircle2, title: "Approve", desc: "Manager reviews and approves in one click" },
              { icon: FileText, title: "Log", desc: "Full audit trail recorded — time saved: 12 min/invoice" },
            ].map((step, idx) => (
              <div key={step.title} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-[#1a56db] text-white flex items-center justify-center text-[14px] font-bold shrink-0">
                    {idx + 1}
                  </div>
                  {idx < 5 && <div className="w-px flex-1 bg-[#e5e7eb] mt-3 min-h-[40px]" />}
                </div>
                <div className="pb-3 pt-1">
                  <div className="flex items-center gap-2">
                    <step.icon className="h-[16px] w-[16px] text-[#1a56db]" />
                    <span className="text-[16px] font-bold text-[#111827]">{step.title}</span>
                  </div>
                  <div className="mt-2 text-[14.5px] text-[#4b5563] leading-[1.6]">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── INDUSTRIES (ONE combined section — 6 cards) ──────────── */
const IndustriesGrid = () => {
  const navigate = useNavigate();
  const industries = [
    {
      key: "education",
      title: "Education",
      desc: "Reduce administrative burden, improve student support coordination, and give school leaders better operational visibility.",
      image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1200&q=80",
      cta: "Explore",
      ready: true,
      comingSoon: false,
    },
    {
      key: "healthcare",
      title: "Healthcare",
      desc: "Streamline patient intake, clinical documentation, scheduling workflows, and compliance reporting.",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80",
      cta: "Learn more",
      ready: false,
      comingSoon: true,
    },
    {
      key: "financial",
      title: "Financial Services",
      desc: "Automate onboarding, regulatory compliance checks, reporting cycles, and client communications.",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80",
      cta: "Learn more",
      ready: false,
      comingSoon: true,
    },
    {
      key: "legal",
      title: "Legal",
      desc: "Accelerate document review, contract management, case research, and billing workflows.",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=80",
      cta: "Learn more",
      ready: false,
      comingSoon: true,
    },
    {
      key: "community-economic-development",
      title: "Community & Economic Development",
      desc: "Prove impact. Strengthen funding. Scale what works. Identify where AI can help community and economic development organizations improve measurable impact, strengthen renewals, support fundraising, and reduce operational friction.",
      image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1200&q=80",
      cta: "Explore Industry",
      ready: true,
      comingSoon: false,
    },
    {
      key: "government",
      title: "Government & Public Sector",
      desc: "Modernize citizen services, permitting workflows, compliance tracking, and inter-agency coordination.",
      image: "https://images.unsplash.com/photo-1555848962-6e79363ec58f?w=1200&q=80",
      cta: "Learn more",
      ready: false,
      comingSoon: true,
    },
  ];

  const openIndustry = (ind) => {
    if (!ind.ready) return;
    navigate(`/industries/${ind.key}`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 50);
  };

  return (
    <section
      id="industries-section"
      className="bg-[#f9fafb] border-y border-[#e5e7eb] py-20 lg:py-28"
      data-testid="industries-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-14">
          <div className={EYEBROW}>Industries we serve</div>
          <h2 className={`${H2} max-w-3xl`}>
            Industry-specific AI implementation
          </h2>
          <p className="mt-5 text-[17px] text-[#4b5563] leading-[1.6] max-w-2xl">
            Every industry has different workflows, compliance needs, and
            operational realities. We tailor our approach to deliver measurable
            results in your specific context.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((ind) => (
            <div
              key={ind.key}
              onClick={() => openIndustry(ind)}
              className={`group relative bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden transition-all hover:shadow-[0_8px_28px_rgba(17,24,39,0.08)] ${ind.ready ? "cursor-pointer" : ""}`}
              data-testid={`industry-tile-${ind.key}`}
            >
              <div className="relative h-52 overflow-hidden bg-[#111827]">
                <img
                  src={ind.image}
                  alt={ind.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-105 transition-all duration-500"
                />
                {ind.comingSoon && (
                  <div className="absolute top-3 right-3 inline-flex items-center rounded-md bg-[#111827]/90 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white">
                    Coming Soon
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#111827]/90 via-[#111827]/60 to-transparent p-5">
                  <h3 className="text-[20px] font-bold text-white tracking-tight">{ind.title}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-[14px] text-[#4b5563] leading-[1.65]">{ind.desc}</p>
                <div className="mt-5 inline-flex items-center gap-1 text-[14px] font-semibold text-[#1a56db] group-hover:gap-2 transition-all">
                  {ind.cta}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── HOW IT WORKS ────────────────────────────────────────── */
const HowItWorks = () => {
  const steps = [
    { n: 1, title: "Discover", desc: "Surface high-ROI workflow opportunities in your operations." },
    { n: 2, title: "Assess", desc: "Quantify impact, effort, and risk with a prioritized action plan." },
    { n: 3, title: "Implement", desc: "Deliver secure workflow automations with milestones and KPIs." },
    { n: 4, title: "Scale", desc: "Validate value and expand across your organization." },
  ];

  return (
    <section id="how-it-works" className="bg-white py-20 lg:py-28" data-testid="how-it-works-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-16 lg:mb-20">
          <div className={EYEBROW}>How it works</div>
          <h2 className={H2}>Four steps to measurable AI value</h2>
          <p className="mt-5 text-[17px] text-[#4b5563] leading-[1.6] max-w-2xl mx-auto">
            Simple, de-risked gates. Most clients begin with the free assessment.
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-[#e5e7eb]" style={{ marginLeft: "12.5%", marginRight: "12.5%" }} />
          <div className="grid md:grid-cols-4 gap-10 md:gap-6 relative">
            {steps.map((s) => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-white border-2 border-[#1a56db] flex items-center justify-center text-[22px] font-bold text-[#1a56db] mb-6 shadow-[0_2px_12px_rgba(26,86,219,0.12)]">
                  {s.n}
                </div>
                <h3 className="text-[20px] font-bold text-[#111827] mb-3">{s.title}</h3>
                <p className="text-[14px] text-[#4b5563] leading-[1.6] max-w-[240px]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── PRICING ─────────────────────────────────────────────── */
const PricingSection = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      tag: "START",
      name: "Preliminary Assessment",
      desc: "Smart intake to identify your top workflow opportunities.",
      price: "Free",
      priceSuffix: "",
      pricePrefix: "",
      features: ["Opportunity Snapshot", "Top 3 workflows", "Readiness indicators", "Recommended next step"],
      cta: "Get My AI Opportunity Score",
      primary: true,
      recommended: false,
      onClick: scrollToIntake,
    },
    {
      tag: "ASSESS",
      name: "In-Depth Assessment",
      desc: "Deep-dive analysis with prioritized action plan and portal setup.",
      price: "",
      priceSuffix: "",
      pricePrefix: "",
      features: ["Workflow analysis", "Prioritized opportunities", "Readiness & security lens", "Action plan", "Portal setup"],
      cta: "Learn More",
      primary: true,
      recommended: true,
      onClick: () => navigate("/services#service-detailed_assessment"),
    },
    {
      tag: "BUILD",
      name: "Phase 1 Implementation",
      desc: "3 high-value workflow implementations with KPI tracking.",
      price: "$9,995",
      priceSuffix: "",
      pricePrefix: "Starting at ",
      features: ["3 workflow automations", "Milestones & timeline", "KPI dashboard", "Value tracking"],
      cta: "Learn More",
      primary: false,
      recommended: false,
      onClick: () => navigate("/services#service-phase_1"),
    },
    {
      tag: "SCALE",
      name: "CAIO Retainer",
      desc: "2 hours/week to maintain and optimize existing AI workflows.",
      price: "$3,995",
      priceSuffix: "/mo",
      pricePrefix: "Starting at ",
      features: ["Monitoring & optimization", "Quarterly roadmap refresh", "1 micro-enhancement/month"],
      cta: "Learn More",
      primary: false,
      recommended: false,
      onClick: () => navigate("/services#service-caio_retainer"),
    },
  ];

  return (
    <section id="pricing-section" className="bg-[#f9fafb] border-y border-[#e5e7eb] py-20 lg:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-14 max-w-3xl">
          <div className={EYEBROW}>Pricing</div>
          <h2 className={H2}>Start free. Scale when ready.</h2>
          <p className="mt-5 text-[17px] text-[#4b5563] leading-[1.6]">
            Most clients begin with the free Preliminary Assessment. We recommend
            the next step based on fit, urgency, and workflow readiness.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-4">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`relative bg-white rounded-2xl p-7 flex flex-col transition-shadow ${t.recommended ? "border-2 border-[#1a56db] shadow-[0_8px_32px_rgba(26,86,219,0.14)]" : "border border-[#e5e7eb] hover:shadow-[0_4px_20px_rgba(17,24,39,0.06)]"}`}
              data-testid={`pricing-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1a56db] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-md">
                  Recommended
                </div>
              )}

              <div className={`text-[11px] font-bold tracking-[0.18em] uppercase mb-4 ${t.recommended ? "text-[#1a56db]" : "text-[#9ca3af]"}`}>
                {t.tag}
              </div>

              <h3 className="text-[20px] font-bold text-[#111827] leading-tight">{t.name}</h3>
              <p className="mt-3 text-[14px] text-[#4b5563] leading-[1.5] min-h-[42px]">{t.desc}</p>

              <div className="mt-7 mb-7">
                {t.pricePrefix && (
                  <div className="text-[13px] font-medium text-[#9ca3af] mb-1">{t.pricePrefix.trim()}</div>
                )}
                <span className="text-[40px] font-bold text-[#111827] tracking-tight">{t.price}</span>
                {t.priceSuffix && (
                  <span className="text-[16px] font-semibold text-[#9ca3af] ml-1">{t.priceSuffix}</span>
                )}
              </div>

              <ul className="space-y-3 mb-7 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[14px] text-[#4b5563]">
                    <Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={t.onClick}
                className={`w-full h-11 rounded-lg text-[14px] font-semibold shadow-none ${t.primary ? "bg-[#1a56db] text-white hover:bg-[#143faa]" : "bg-[#f9fafb] border border-[#e5e7eb] text-[#111827] hover:bg-[#f3f4f6]"}`}
                data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {t.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── FROM OUR FOUNDER ────────────────────────────────────── */
const FounderSection = () => (
  <section className="bg-[#0f172a] py-20 lg:py-28" data-testid="founder-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-12 lg:gap-16 items-center">
        {/* Book cover — actual book image */}
        <div className="flex justify-center lg:justify-start">
          <div className="relative w-[300px] rounded-xl overflow-hidden shadow-[0_30px_70px_rgba(0,0,0,0.55)]">
            <img
              src="https://customer-assets.emergentagent.com/job_10e54cf2-fdea-445f-8f7f-2c90b2bf106c/artifacts/55g25tn1_AI%20Book%20Cover%2003202026.png"
              alt="AI for CEOs — The Executive Playbook by Dirk DaCosta"
              className="w-full h-auto block"
              data-testid="founder-book-cover-image"
            />
          </div>
        </div>

        {/* Quote + CTA */}
        <div>
          <div className="inline-flex items-center gap-2 text-[#60a5fa] text-[12px] font-semibold tracking-[0.16em] uppercase mb-5">
            <BookOpen className="h-4 w-4" />
            From Our Founder
          </div>
          <h2 className="text-[30px] lg:text-[38px] font-bold text-white leading-[1.15] tracking-tight">
            AI for CEOs
          </h2>
          <p className="mt-3 text-[16px] text-white/70">
            The Executive Playbook for Turning Artificial Intelligence into Real Business Growth
          </p>
          <p className="mt-1 text-[14px] text-white/50">By Dirk DaCosta</p>

          <blockquote className="mt-8 border-l-2 border-[#1a56db] pl-6 text-[16px] italic text-white/85 leading-[1.8] max-w-2xl">
            "The promise of AI is not that it replaces the value of leadership.
            It is that it expands what strong leadership can accomplish. The
            businesses that win in this next era will not be the ones that
            collect the most AI tools. They will be the ones that understand
            their workflows, identify their bottlenecks, apply AI where it
            creates real leverage, and build the discipline to scale what
            works."
          </blockquote>

          <div className="mt-6 text-[13px] text-white/60">
            <span className="font-semibold text-white/80">Dirk DaCosta</span>
            <span className="mx-2">·</span>
            Founder, 589 Labs.ai
          </div>

          <a
            href="https://www.amazon.com/gp/product/B0GYVS895K/ref=ox_sc_act_image_1?smid=A1Y53T3O3Q25L8&psc=1"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 h-11 rounded-lg bg-white px-6 text-[14px] font-semibold text-[#111827] hover:bg-white/90 transition-colors"
            data-testid="founder-book-cta"
          >
            <BookOpen className="h-4 w-4" />
            Get the Book
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ─── INTAKE / AI OPPORTUNITY SCORE (with calc) ──────────── */
/* ─── Demo profiles for the public intake form (admin-only Cmd+Shift+D) ─────
 * Industry-keyed sample data filled into all remaining fields when the
 * consultant presses Cmd+Shift+D AFTER selecting an industry.
 * The user never sees these profiles — they only see field values populate.
 * ───────────────────────────────────────────────────────────────────── */
const INTAKE_DEMO_PROFILES = {
  education: {
    company_name: "Summit Grove School Network",
    contact_name: "Ethan Blake",
    contact_email: "ethan.blake@summitgrovecc.demo",
    role: "Director of Operations",
    employee_count: "51-200",
    website: "summitgrove.edu",
    phone: "(609) 555-0142",
    pain_point: "Admissions and parent communications are largely manual. The team re-keys application data into multiple systems, and parent inquiries are split across email, voicemail, and a portal — so response time has stretched to 48–72 hours during peak periods. Leadership wants more visibility into where families drop off in the application journey.",
  },
  healthcare: {
    company_name: "Cedar Health Partners",
    contact_name: "Dr. Priya Anand",
    contact_email: "panand@cedarhealth.demo",
    role: "Chief Medical Officer",
    employee_count: "201-500",
    website: "cedarhealth.demo",
    phone: "(609) 555-0118",
    pain_point: "Patient intake forms feed three different systems with manual reconciliation. Care coordinators spend the majority of their time on documentation rather than direct patient outreach. Reporting to payers requires repeated manual cleanup before submission.",
  },
  financial_services: {
    company_name: "Northbridge Wealth Advisors",
    contact_name: "Marcus Ortega",
    contact_email: "mortega@northbridge.demo",
    role: "Managing Director",
    employee_count: "51-200",
    website: "northbridgewealth.demo",
    phone: "(212) 555-0173",
    pain_point: "Client onboarding is manual, multi-step, and takes 7–14 days. Our advisors spend significant time pulling together quarterly client reviews from three different platforms. Compliance review cycles are unpredictable, which slows client communications.",
  },
  legal: {
    company_name: "Hollis & Burke LLP",
    contact_name: "Sarah Hollis",
    contact_email: "shollis@hollisburke.demo",
    role: "Managing Partner",
    employee_count: "51-200",
    website: "hollisburke.demo",
    phone: "(212) 555-0146",
    pain_point: "Contract review is the largest drag on associate hours — first-pass review of standard NDAs and MSAs eats 6–10 hours per week per associate. Matter status reporting is also inconsistent across the firm; some partners get weekly summaries, others have to chase.",
  },
  community_economic_development: {
    company_name: "Cedar Valley Community Partnership",
    contact_name: "Alyssa Grant",
    contact_email: "alyssa.grant@cedarvalleycp.demo",
    role: "Executive Director",
    employee_count: "51-200",
    website: "cedarvalleycp.demo",
    phone: "(609) 555-0177",
    pain_point: "We are running too lean to keep up with the level of proof, visibility, and responsiveness funders expect. Grant and RFP pursuit depends too heavily on individual staff knowledge. Impact reporting takes weeks to compile because data sits in three different program systems. Leadership has no single view of deadlines, owners, or red flags across the portfolio.",
  },
  government_public_sector: {
    company_name: "Mercer County Workforce Authority",
    contact_name: "James Whitfield",
    contact_email: "jwhitfield@mercerwfa.demo",
    role: "Deputy Director",
    employee_count: "201-500",
    website: "mercerwfa.demo",
    phone: "(609) 555-0190",
    pain_point: "Resident intake, eligibility checks, and case follow-up are managed across spreadsheets, paper forms, and three legacy systems. Funding source compliance reports take a small team 5–7 days each month to compile, and we are constantly short on time for direct service.",
  },
  professional_services: {
    company_name: "Apex Strategy Group",
    contact_name: "Renee Castillo",
    contact_email: "rcastillo@apexstrategy.demo",
    role: "Chief Operating Officer",
    employee_count: "51-200",
    website: "apexstrategy.demo",
    phone: "(212) 555-0129",
    pain_point: "Engagement status reporting is inconsistent across consultants and engagements. Client deliverable prep takes longer than it should because senior consultants are pulled into formatting. Capacity planning lives in a spreadsheet that nobody trusts.",
  },
  other: {
    company_name: "Acme Operations Co.",
    contact_name: "Alex Morgan",
    contact_email: "amorgan@acme.demo",
    role: "VP of Operations",
    employee_count: "51-200",
    website: "acme.demo",
    phone: "(609) 555-0100",
    pain_point: "Our team spends significant time on repetitive admin work, manual data reconciliation, and chasing status across tools. Leadership wants better visibility and faster response time without adding headcount.",
  },
};
INTAKE_DEMO_PROFILES.default = INTAKE_DEMO_PROFILES.other;


const IntakeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const companyInputRef = useRef(null);

  // Read industry prefill synchronously at mount (from URL ?industry= or nav state)
  const initialIndustry = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlIndustry = params.get("industry");
      if (urlIndustry) return urlIndustry;
    } catch {}
    return "";
  })();

  const [formData, setFormData] = useState({
    industry: initialIndustry,
    company_name: "",
    contact_name: "",
    contact_email: "",
    role: "",
    employee_count: "",
    website: "",
    phone: "",
    pain_point: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  // If user lands with ?industry=X (or via state), focus Company Name input after scroll.
  useEffect(() => {
    const searchIndustry = new URLSearchParams(location.search).get("industry");
    const preIndustry = location.state?.prefillIndustry || searchIndustry;
    if (preIndustry) {
      setFormData((p) => (p.industry === preIndustry ? p : { ...p, industry: preIndustry }));
      setTimeout(() => {
        companyInputRef.current?.focus();
      }, 700);
    }
  }, [location.state, location.search]);

  const INDUSTRY_LABEL = {
    education: "Education",
    healthcare: "Healthcare",
    financial_services: "Financial Services",
    legal: "Legal",
    "community_economic_development": "Community & Economic Development",
    "government_public_sector": "Government & Public Sector",
    professional_services: "Professional Services",
    other: "Your industry",
  };

  // ── Admin demo: simple visible button. No keyboard shortcut, no auth gate.
  //   Only thing we check is the localStorage flag set by /app sidebar toggle.
  //   Prospects never have the flag set, so they never see the button.
  // ──────────────────────────────────────────────────────────────────────
  const [demoArmed, setDemoArmed] = useState(false);
  useEffect(() => {
    const sync = () => {
      try {
        setDemoArmed(window.localStorage.getItem("clarity_admin_demo_mode_on") === "1");
      } catch {
        setDemoArmed(false);
      }
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("clarity-admin-demo-mode-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("clarity-admin-demo-mode-changed", sync);
    };
  }, []);

  const triggerDemoFill = () => {
    setFormData((p) => {
      if (!p.industry) {
        toast.warning("Pick an industry first — then click Demo Fill");
        return p;
      }
      const fill = INTAKE_DEMO_PROFILES[p.industry] || INTAKE_DEMO_PROFILES.default;
      const merged = {
        ...p,
        company_name: p.company_name || fill.company_name,
        contact_name: p.contact_name || fill.contact_name,
        contact_email: p.contact_email || fill.contact_email,
        role: p.role || fill.role,
        employee_count: p.employee_count || fill.employee_count,
        website: p.website || fill.website,
        phone: p.phone || fill.phone,
        pain_point: p.pain_point || fill.pain_point,
      };
      toast.success(`Filled ${INDUSTRY_LABEL[p.industry] || p.industry} sample data — click Get My AI Opportunity Score to submit`);
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API}/api/admin/demo/audit`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ feature: "opportunity_form_autofill", extra: { industry: p.industry } }),
        }).catch(() => {});
      } catch {}
      return merged;
    });
  };

  const computeSnapshot = (data) => {
    const industryLabel = INDUSTRY_LABEL[data.industry] || "Your industry";
    return {
      industryLabel,
      opportunities: [
        {
          title: `${industryLabel} Intake Automation`,
          description: `Automate initial data capture and routing for incoming requests, reducing manual entry by 60–80%.`,
          impact: "High impact",
        },
        {
          title: "Approval Workflow Acceleration",
          description: "Streamline multi-step approvals with smart routing, escalation rules, and mobile notifications.",
          impact: "Medium-high impact",
        },
        {
          title: "Document Processing Pipeline",
          description: "Extract, validate, and route information from documents automatically with human oversight.",
          impact: "Medium impact",
        },
      ],
      recommendation: `Based on your ${data.employee_count || "organization"} size and ${industryLabel} focus, we recommend starting with an In-Depth Assessment to map your specific workflows and build a prioritized implementation roadmap.`,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.industry || !formData.company_name || !formData.contact_name || !formData.contact_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Fire-and-forget CRM lead
      try {
        await crmApi.createLead(
          {
            vertical: formData.industry,
            company: formData.company_name,
            full_name: formData.contact_name,
            email: formData.contact_email,
            role: formData.role,
            company_size: formData.employee_count,
            website: formData.website,
            phone: formData.phone,
            pain_areas: [],
            notes: formData.pain_point,
          },
          null,
        );
      } catch (_) { /* non-blocking */ }

      // 2) Analytics
      trackEvent("form_submit", { form: "opportunity_score" });
      analyticsApi.track("form_submit", { form: "opportunity_score" });

      // 3) Optional back-end intake log
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${API}/api/intake`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      } catch (_) { /* non-blocking */ }

      // 4) Compute & show Snapshot results
      setSnapshot(computeSnapshot(formData));
      setTimeout(() => {
        const el = document.getElementById("opportunity-score-results");
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key, val) => setFormData((p) => ({ ...p, [key]: val }));
  const resetAll = () => {
    setSnapshot(null);
    setFormData({
      industry: "", company_name: "", contact_name: "",
      contact_email: "", role: "", employee_count: "",
      website: "", phone: "", pain_point: "",
    });
  };

  const labelClass = "block text-[11px] font-semibold tracking-[0.12em] text-[#111827] uppercase mb-2";
  const inputClass = "h-12 rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[15px] placeholder:text-[#9ca3af]";

  if (snapshot) {
    return (
      <section id="opportunity-score" className="bg-white py-20 lg:py-28" data-testid="intake-section">
        <div id="opportunity-score-results" className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 scroll-mt-24" data-testid="score-results">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#ecfdf5] px-4 py-1.5 text-[13px] font-semibold text-[#059669] mb-4">
              <CheckCircle2 className="h-4 w-4" />
              Opportunity Snapshot Ready
            </div>
            <h2 className={H2}>Your AI Opportunity Snapshot</h2>
            <p className="mt-4 text-[16px] text-[#4b5563] max-w-xl mx-auto">
              Based on your intake for <span className="font-semibold text-[#111827]">{snapshot.industryLabel}</span>, here are your top 3 workflow opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {snapshot.opportunities.map((opp, idx) => (
              <div key={idx} className="bg-white border border-[#e5e7eb] rounded-2xl p-6" data-testid={`score-opp-${idx}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-[#e8eefb] text-[#1a56db] text-[14px] font-bold flex items-center justify-center">{idx + 1}</div>
                  <div className="text-[15px] font-bold text-[#111827]">{opp.title}</div>
                </div>
                <p className="text-[14px] text-[#4b5563] leading-[1.6]">{opp.description}</p>
                <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#ecfdf5] px-3 py-1 text-[11px] font-semibold text-[#059669]">
                  <Sparkles className="h-3 w-3" />
                  {opp.impact}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border-2 border-[#1a56db] bg-[#e8eefb]/40 p-6 flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-[#1a56db] text-white flex items-center justify-center shrink-0">
              <ArrowRight className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[13px] font-bold tracking-[0.14em] text-[#1a56db] uppercase">Recommended Next Step</div>
              <p className="mt-2 text-[15px] text-[#111827] leading-[1.6]">{snapshot.recommendation}</p>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border-2 border-[#1a56db] bg-white p-6 text-center shadow-[0_8px_32px_rgba(26,86,219,0.12)]" data-testid="book-call-cta">
            <Calendar className="h-8 w-8 text-[#1a56db] mx-auto mb-3" />
            <h4 className="text-[18px] font-bold text-[#111827]">Book a FREE Qualification Call</h4>
            <p className="mt-2 text-[14px] text-[#4b5563] max-w-md mx-auto">
              Speak with a consultant to discuss your results and explore how AI can transform your workflows.
            </p>
            <Button
              onClick={() => {
                trackEvent("booked_call", { source: "score_results" });
                analyticsApi.track("booked_call", { source: "score_results" });
                navigate("/contact?intent=book", {
                  state: {
                    vertical: formData.industry,
                    company: formData.company_name,
                    name: formData.contact_name,
                    email: formData.contact_email,
                    notes: formData.pain_point,
                  },
                });
                setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
              }}
              className="mt-5 h-12 rounded-lg bg-[#1a56db] px-8 text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
              data-testid="book-call-btn"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Schedule My Free Call
            </Button>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              onClick={resetAll}
              className="h-10 rounded-lg border-[#e5e7eb] text-[#111827] text-[13px] font-medium hover:bg-[#f9fafb] shadow-none"
              data-testid="score-reset-btn"
            >
              Start Over
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="opportunity-score" className="bg-white py-20 lg:py-28" data-testid="intake-section">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 scroll-mt-24">
        <div className="text-center mb-14">
          <div className={EYEBROW}>Get started</div>
          <h2 className={H2}>Get your AI Opportunity Score</h2>
          <p className="mt-5 text-[17px] text-[#4b5563] leading-[1.7] max-w-xl mx-auto">
            Complete the intake below to receive your Opportunity Snapshot with
            top workflow opportunities and recommended next steps.
          </p>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} data-testid="intake-form">
          {demoArmed && (
            <div className="mb-6 flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-[#007a9e] bg-[#f0f9fc] px-4 py-3" data-testid="demo-armed-indicator">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#007a9e]" />
                <span className="text-[13px] font-semibold text-[#00485f]">Demo Mode is ON — pick an industry, then click</span>
              </div>
              <Button
                type="button"
                onClick={triggerDemoFill}
                className="h-9 rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white text-[13px] font-semibold px-4 shadow-none"
                data-testid="demo-fill-btn"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Demo Fill
              </Button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Industry *</label>
              <Select key={`industry-${formData.industry}`} value={formData.industry || undefined} onValueChange={(v) => set("industry", v)}>
                <SelectTrigger className={inputClass} data-testid="intake-industry">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INDUSTRY_LABEL).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelClass}>Company name *</label>
              <Input
                ref={companyInputRef}
                value={formData.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                className={inputClass}
                placeholder="e.g., Acme Healthcare Group"
                data-testid="intake-company-name"
              />
            </div>
            <div>
              <label className={labelClass}>Your name *</label>
              <Input
                value={formData.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                className={inputClass}
                placeholder="e.g., Taylor Morgan"
                data-testid="intake-your-name"
              />
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                className={inputClass}
                placeholder="taylor@company.com"
                data-testid="intake-email"
              />
            </div>
            <div>
              <label className={labelClass}>Role / Title</label>
              <Input
                value={formData.role}
                onChange={(e) => set("role", e.target.value)}
                className={inputClass}
                placeholder="e.g., COO, Director of Operations"
                data-testid="intake-role"
              />
            </div>
            <div>
              <label className={labelClass}>Company size</label>
              <Select value={formData.employee_count} onValueChange={(v) => set("employee_count", v)}>
                <SelectTrigger className={inputClass} data-testid="intake-company-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {["1-50", "51-200", "201-500", "501-1000", "1000+"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelClass}>Website</label>
              <Input
                value={formData.website || ""}
                onChange={(e) => set("website", e.target.value)}
                className={inputClass}
                placeholder="yourcompany.com"
                data-testid="intake-website"
              />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <PhoneField
                value={formData.phone || ""}
                onChange={(v) => set("phone", v)}
                placeholder="(555) 000-0000"
                testid="intake-phone"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className={labelClass}>What workflow should we look at first?</label>
            <Textarea
              value={formData.pain_point}
              onChange={(e) => set("pain_point", e.target.value)}
              className="rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[15px] placeholder:text-[#9ca3af] min-h-[110px] resize-y"
              placeholder="Describe the process causing the most friction..."
              data-testid="intake-pain-point"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full h-14 rounded-xl bg-[#1a56db] text-white text-[15px] font-semibold hover:bg-[#143faa] shadow-none"
            data-testid="intake-submit-btn"
          >
            {submitting ? "Calculating your score..." : "Get My AI Opportunity Score"}
            {!submitting && <ArrowRight className="ml-2 h-[18px] w-[18px]" />}
          </Button>

          <p className="mt-5 text-center text-[13px] text-[#9ca3af]">
            By submitting, you agree we may contact you about next steps.
          </p>
        </form>
      </div>
    </section>
  );
};

/* ─── MAIN PAGE ───────────────────────────────────────────── */
const HomePage = () => {
  return (
    <PageShell>
      <HeroSection />
      <TrustBar />
      <AIOrchestrationGraphic />
      <WhatWeBuild />
      <IndustriesGrid />
      <HowItWorks />
      <PricingSection />
      <FounderSection />
      <IntakeForm />
    </PageShell>
  );
};

export default HomePage;
